import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/**
 * Generate 90 days of sample traffic data
 */
const generateSampleData = () => {
  const data = [];
  const start = new Date();
  start.setDate(start.getDate() - 90);

  for (let i = 0; i < 90; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    const users = Math.floor(Math.random() * 500) + 100;
    const sessions = Math.floor(users * (1.1 + Math.random() * 0.4));
    const newUsers = Math.floor(users * 0.6);

    data.push({
      date: date.toISOString(),
      users,
      sessions,
      newUsers,
    });
  }
  return data;
};

/**
 * Get accurate week number for grouping
 */
const getWeekNumber = (d) => {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const week1 = new Date(date.getFullYear(), 0, 4);
  return `${date.getFullYear()}-W${1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7)}`;
};

/**
 * Get Month-Year key for grouping
 */
const getMonthYear = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * VisitorsChart Component
 * 
 * Shows users and sessions over time with aggregation controls.
 * Pre-populates with 90 days of sample data if no prop is provided.
 */
const VisitorsChart = ({ data }) => {
  const [range, setRange] = useState('daily');

  // Load raw data or fallback to sample
  const rawData = useMemo(() => {
    return (data && data.length > 0) ? data : generateSampleData();
  }, [data]);

  // Aggregate data based on current toggle (Daily, Weekly, Monthly)
  const aggregatedData = useMemo(() => {
    // If we have real project data (passed from parent), we skip internal aggregation
    if (data && data.length > 0) {
      return data.map(d => ({
        ...d,
        formattedDate: d.date // Backend already formats it (HH:00 or YYYY-MM-DD)
      }));
    }

    if (range === 'daily') {
      return rawData.map(d => ({
        ...d,
        formattedDate: new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(d.date))
      }));
    }

    const groups = {};
    rawData.forEach(d => {
      const dateObj = new Date(d.date);
      const key = range === 'weekly' ? getWeekNumber(dateObj) : getMonthYear(dateObj);
      
      if (!groups[key]) {
        groups[key] = {
          date: d.date,
          users: 0,
          sessions: 0,
          newUsers: 0,
          count: 0
        };
      }
      groups[key].users += d.users;
      groups[key].sessions += d.sessions;
      groups[key].newUsers += d.newUsers;
      groups[key].count += 1;
    });

    return Object.keys(groups).sort().map(key => {
      const group = groups[key];
      let formatted = '';
      if (range === 'weekly') {
        const d = new Date(group.date);
        formatted = `Week of ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d)}`;
      } else {
        formatted = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(new Date(group.date));
      }
      return {
        ...group,
        formattedDate: formatted
      };
    });
  }, [rawData, range]);

  // Calculated Stats for KPI Strip
  const stats = useMemo(() => {
    const totalUsers = rawData.reduce((acc, d) => acc + d.users, 0);
    const totalSessions = rawData.reduce((acc, d) => acc + d.sessions, 0);
    const avgDailyUsers = Math.round(totalUsers / rawData.length);
    return { totalUsers, totalSessions, avgDailyUsers };
  }, [rawData]);

  /**
   * Format numbers to K abbreviation
   */
  const formatYAxis = (value) => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value;
  };

  /**
   * Custom Tooltip Component
   */
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.98)', borderRadius: 2, border: '1px solid #eee' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>{label}</Typography>
          <Box sx={{ minWidth: 120 }}>
            {payload.map((entry, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: entry.color, mr: 1, borderRadius: '50%' }} />
                  <Typography variant="body2" sx={{ mr: 2 }}>{entry.name}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {new Intl.NumberFormat().format(entry.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {/* KPI Strip */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: 'primary.main' }} />
            <CardContent>
              <Typography color="text.secondary" variant="subtitle2" gutterBottom>Total Users</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {new Intl.NumberFormat().format(stats.totalUsers)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: 'success.main' }} />
            <CardContent>
              <Typography color="text.secondary" variant="subtitle2" gutterBottom>Total Sessions</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {new Intl.NumberFormat().format(stats.totalSessions)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: 'warning.main' }} />
            <CardContent>
              <Typography color="text.secondary" variant="subtitle2" gutterBottom>Avg. Daily Users</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {new Intl.NumberFormat().format(stats.avgDailyUsers)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart Section */}
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Visitors over time</Typography>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={(e, next) => next && setRange(next)}
            size="small"
            color="primary"
          >
            <ToggleButton value="daily" sx={{ px: 2, fontWeight: 600 }}>Daily</ToggleButton>
            <ToggleButton value="weekly" sx={{ px: 2, fontWeight: 600 }}>Weekly</ToggleButton>
            <ToggleButton value="monthly" sx={{ px: 2, fontWeight: 600 }}>Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={aggregatedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis 
                dataKey="formattedDate" 
                tick={{ fontSize: 12, fill: '#94a3b8' }} 
                tickLine={false} 
                axisLine={false} 
                dy={10}
              />
              <YAxis 
                tickFormatter={formatYAxis} 
                tick={{ fontSize: 12, fill: '#94a3b8' }} 
                tickLine={false} 
                axisLine={false} 
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
              <Line 
                type="monotone" 
                dataKey="users" 
                name="Users" 
                stroke="#1976d2" 
                strokeWidth={3} 
                dot={range === 'daily' ? false : { r: 4, fill: '#1976d2', strokeWidth: 2, stroke: '#fff' }} 
                activeDot={{ r: 6, strokeWidth: 0 }} 
              />
              <Line 
                type="monotone" 
                dataKey="sessions" 
                name="Sessions" 
                stroke="#2e7d32" 
                strokeWidth={3} 
                dot={range === 'daily' ? false : { r: 4, fill: '#2e7d32', strokeWidth: 2, stroke: '#fff' }} 
                activeDot={{ r: 6, strokeWidth: 0 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default VisitorsChart;
